import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalSwitch } from '../../../components/GlobalComponents/GlobalSwitch';

interface FeeReminderSchedule {
  enabled: boolean;
  days: number[];
  afterDayOfMonth: number;
  customDates: string[];
  mode: 'weekly' | 'custom';
  dailyLimit: number;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_SCHEDULE: FeeReminderSchedule = {
  enabled: true,
  days: [1, 4],
  afterDayOfMonth: 5,
  customDates: [],
  mode: 'weekly',
  dailyLimit: 1,
};

const chipStyle = (
  selected: boolean,
  disabled = false,
  selectedColor: { border: string; bg: string; color: string; shadow: string } = {
    border: '#3b82f6', bg: '#eff6ff', color: '#1e40af', shadow: 'rgba(59,130,246,0.15)',
  }
): React.CSSProperties => ({
  padding: '8px 16px', borderRadius: '8px',
  border: selected ? `2px solid ${selectedColor.border}` : '2px solid #e5e7eb',
  background: selected ? selectedColor.bg : disabled ? '#fafafa' : 'white',
  color: selected ? selectedColor.color : disabled ? '#d1d5db' : '#374151',
  fontWeight: selected ? '600' : '500', fontSize: '13px',
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: '6px', boxShadow: selected ? `0 2px 8px ${selectedColor.shadow}` : 'none',
  userSelect: 'none' as const, whiteSpace: 'nowrap' as const,
});

const FeesReminderSettings: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<FeeReminderSchedule>(DEFAULT_SCHEDULE);
  const [calendarDates, setCalendarDates] = useState<Nullable<Date[]>>(null);
  const [savedSchedule, setSavedSchedule] = useState<FeeReminderSchedule | null>(null);
  const [showFeeAmount, setShowFeeAmount] = useState<boolean>(true);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiClient.get('/options/fee_reminder_schedule');
      if (response.data.status === 'success' && response.data.data) {
        const saved: FeeReminderSchedule = response.data.data;
        setSchedule(saved);
        setSavedSchedule(saved);
        if (saved.customDates?.length > 0) {
          setCalendarDates(saved.customDates.map((d) => new Date(d)));
        }
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load fee reminder settings', life: 4000 });
      }
    }
    try {
      const res = await apiClient.get('/options/show_fee_amount');
      if (res.data.status === 'success' && res.data.data !== undefined) {
        const val = res.data.data;
        setShowFeeAmount(val === true || val === 1 || val === '1');
      }
    } catch { /* use default true */ }
    finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (schedule.mode === 'weekly' && schedule.days.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select at least one day of the week', life: 4000 });
      return;
    }
    if (schedule.mode === 'custom' && schedule.customDates.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select at least one date', life: 4000 });
      return;
    }
    setSaving(true);
    try {
      // Save schedule
      const checkSchedule = await apiClient.get('/options/fee_reminder_schedule').catch(() => null);
      const scheduleExists = checkSchedule?.data?.status === 'success';
      let response;
      if (scheduleExists) {
        response = await apiClient.put('/options/fee_reminder_schedule', { value: schedule });
      } else {
        response = await apiClient.post('/options', { name: 'fee_reminder_schedule', value: schedule, autoload: 'yes' });
      }
      if (response.data.status !== 'success') throw new Error(response.data.message || 'Failed to save');

      // ✅ Always PUT first, fallback to POST — bypasses broken unserialize check
      const feeAmountValue = showFeeAmount ? 'b:1;' : 'b:0;';
      const putRes = await apiClient.put('/options/show_fee_amount', { value: feeAmountValue });
      if (putRes.data.status !== 'success') {
        await apiClient.post('/options', { name: 'show_fee_amount', value: feeAmountValue, autoload: 'yes' });
      }

      setSavedSchedule({ ...schedule });
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Settings saved successfully', life: 4000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to save settings', life: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: number) => {
    setSchedule((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day].sort(),
    }));
  };

  const handleCalendarChange = (dates: Nullable<Date[]>) => {
    setCalendarDates(dates);
    if (!dates || dates.length === 0) { setSchedule((prev) => ({ ...prev, customDates: [] })); return; }
    const formatted = dates.map((d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    });
    setSchedule((prev) => ({ ...prev, customDates: formatted }));
  };

  const removeCustomDate = (dateStr: string) => {
    const remaining = schedule.customDates.filter((d) => d !== dateStr);
    setSchedule((prev) => ({ ...prev, customDates: remaining }));
    setCalendarDates(remaining.length > 0 ? remaining.map((d) => new Date(d)) : null);
  };

  const clearAllDates = () => { setSchedule((prev) => ({ ...prev, customDates: [] })); setCalendarDates(null); };

  const formatDisplayDate = (dateStr: string): string =>
    new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const ordinalSuffix = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd']; const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  if (loading) {
    return (
      <div style={{ background: 'white', borderRadius: '16px', padding: '60px 24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(30,64,175,0.08)', border: '1px solid rgba(59,130,246,0.12)', textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading fee reminder settings...</p>
        <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      {/* Fee Reminder Schedule Card */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(30,64,175,0.08)', border: !schedule.enabled ? '1px solid #fcd34d' : '1px solid rgba(59,130,246,0.12)', transition: 'border-color 0.2s' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: !schedule.enabled ? '2px solid #fde68a' : '2px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: !schedule.enabled ? 'linear-gradient(135deg,#d97706 0%,#fbbf24 100%)' : 'linear-gradient(135deg,#059669 0%,#34d399 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>
              <i className={!schedule.enabled ? 'pi pi-ban' : 'pi pi-bell'} style={{ fontSize: '18px', color: 'white' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af', margin: 0 }}>Fee Reminder Schedule</h2>
                {!schedule.enabled && <span style={{ fontSize: '11px', fontWeight: '600', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '20px', padding: '2px 10px' }}>⚠ Disabled</span>}
                {schedule.enabled && <span style={{ fontSize: '11px', fontWeight: '600', background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: '20px', padding: '2px 10px' }}>✓ Active</span>}
              </div>
              <p style={{ fontSize: '13px', color: !schedule.enabled ? '#92400e' : '#64748b', margin: '2px 0 0 0' }}>
                {!schedule.enabled ? 'Fee reminders are disabled — toggle to activate' : 'Configure when fee reminder notifications are sent to students'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{schedule.enabled ? 'Enabled' : 'Disabled'}</span>
              <GlobalSwitch value={schedule.enabled} onChange={() => setSchedule((prev) => ({ ...prev, enabled: !prev.enabled }))} />
            </div>
            <GlobalButton label={saving ? 'Saving...' : 'Save Schedule'} onClick={handleSave} disabled={saving} loading={saving} icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} variant="success" size="small" />
          </div>
        </div>

        {!savedSchedule && (
          <div style={{ background: '#fffbeb', borderRadius: '10px', padding: '12px 16px', border: '1px solid #fcd34d', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', fontSize: '15px', marginTop: '1px' }} />
            <div>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', margin: '0 0 4px 0' }}>No custom schedule saved — using system defaults</p>
              <p style={{ fontSize: '12px', color: '#78350f', margin: 0, lineHeight: 1.6 }}>Fee reminders are currently sent every <strong>Monday and Thursday</strong>, but only after the <strong>5th of each month</strong>.</p>
            </div>
          </div>
        )}

        {savedSchedule && (
          <div style={{ background: '#fffbeb', borderRadius: '10px', padding: '12px 16px', border: '1px solid #fcd34d', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <i className="pi pi-bookmark-fill" style={{ color: '#d97706', fontSize: '15px', marginTop: '1px' }} />
            <div>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', margin: '0 0 4px 0' }}>Currently saved settings</p>
              <p style={{ fontSize: '12px', color: '#78350f', margin: 0 }}>
                {savedSchedule.mode === 'weekly'
                  ? `Mode: Weekly — sends on ${savedSchedule.days.map((d) => DAY_FULL[d]).join(' and ')}, after the ${ordinalSuffix(savedSchedule.afterDayOfMonth)} of each month. Up to ${savedSchedule.dailyLimit ?? 1}× per day. Status: ${savedSchedule.enabled ? 'Enabled' : 'Disabled'}`
                  : `Mode: Specific Dates — ${savedSchedule.customDates.length} date${savedSchedule.customDates.length !== 1 ? 's' : ''} selected. Up to ${savedSchedule.dailyLimit ?? 1}× per day. Status: ${savedSchedule.enabled ? 'Enabled' : 'Disabled'}`}
              </p>
            </div>
          </div>
        )}

        <div style={{ opacity: schedule.enabled ? 1 : 0.4, pointerEvents: schedule.enabled ? 'all' : 'none', transition: 'opacity 0.2s' }}>
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>Notification Mode</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {(['weekly', 'custom'] as const).map((mode) => (
                <button key={mode} onClick={() => setSchedule((prev) => ({ ...prev, mode }))} style={chipStyle(schedule.mode === mode)}>
                  <i className={mode === 'weekly' ? 'pi pi-calendar-times' : 'pi pi-calendar'} style={{ fontSize: '13px' }} />
                  {mode === 'weekly' ? 'Weekly Days' : 'Specific Dates'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              <i className="pi pi-send" style={{ marginRight: '6px', color: '#3b82f6' }} />How many times per day
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>Maximum number of fee reminder notifications sent per day. Default is 1. Max allowed is 3.</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {[{ count: 1, label: '1×', sub: 'once' }, { count: 2, label: '2×', sub: 'twice' }, { count: 3, label: '3×', sub: '3 times' }].map(({ count, label, sub }) => (
                <button key={count} onClick={() => setSchedule((prev) => ({ ...prev, dailyLimit: count }))} style={chipStyle((schedule.dailyLimit ?? 1) === count)}>
                  <span style={{ fontWeight: '700', fontSize: '14px' }}>{label}</span>
                  <span style={{ fontSize: '11px', opacity: 0.8, fontWeight: '400' }}>{sub}</span>
                </button>
              ))}
            </div>
            <p style={{ fontSize: '11px', color: '#4b5563', marginTop: '8px' }}>
              {(schedule.dailyLimit ?? 1) === 1 ? 'Students will receive 1 reminder on scheduled days.' : `Students will receive up to ${schedule.dailyLimit} reminders spread throughout scheduled days.`}
            </p>
          </div>

          {schedule.mode === 'weekly' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 14px 0' }}>
                  <i className="pi pi-calendar-times" style={{ marginRight: '6px', color: '#3b82f6' }} />Send reminders on these days
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {DAY_LABELS.map((label, idx) => (
                    <button key={idx} onClick={() => toggleDay(idx)} title={DAY_FULL[idx]} style={chipStyle(schedule.days.includes(idx))}>{label}</button>
                  ))}
                </div>
                <p style={{ fontSize: '11px', color: '#4b5563', margin: '10px 0 0 0' }}>Selected: {schedule.days.map((d) => DAY_FULL[d]).join(', ') || 'None'}</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 6px 0' }}>
                  <i className="pi pi-filter" style={{ marginRight: '6px', color: '#3b82f6' }} />Only send after day of month
                </p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 14px 0' }}>Reminders trigger only on selected days <strong>after</strong> this date each month.</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[1,2,3,4,5,6,7,8,9,10].map((day) => (
                    <button key={day} onClick={() => setSchedule((prev) => ({ ...prev, afterDayOfMonth: day }))} style={chipStyle(schedule.afterDayOfMonth === day, false, { border: '#059669', bg: '#f0fdf4', color: '#065f46', shadow: 'rgba(5,150,105,0.15)' })}>
                      {ordinalSuffix(day)}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '11px', color: '#4b5563', margin: '10px 0 0 0' }}>Currently set to: after the <strong>{ordinalSuffix(schedule.afterDayOfMonth)}</strong> of each month</p>
              </div>
              {schedule.days.length > 0 && (
                <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '14px 18px', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <i className="pi pi-info-circle" style={{ color: '#3b82f6', fontSize: '16px', marginTop: '1px' }} />
                  <div>
                    <p style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600', margin: '0 0 4px 0' }}>Schedule Summary</p>
                    <p style={{ fontSize: '12px', color: '#1e40af', margin: 0 }}>
                      Fee reminders will be sent every <strong>{schedule.days.map((d) => DAY_FULL[d]).join(' and ')}</strong>, but only after the <strong>{ordinalSuffix(schedule.afterDayOfMonth)}</strong> of each month.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {schedule.mode === 'custom' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 4px 0' }}>
                  <i className="pi pi-calendar" style={{ marginRight: '6px', color: '#3b82f6' }} />Pick specific dates
                </p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px 0' }}>Click dates on the calendar to select. Fee reminders will only be sent on selected dates.</p>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Calendar value={calendarDates} onChange={(e) => handleCalendarChange(e.value ?? null)} inline selectionMode="multiple" minDate={new Date()} panelStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px 0' }}>Selected dates ({schedule.customDates.length})</p>
                    {schedule.customDates.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', background: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                        <i className="pi pi-calendar" style={{ fontSize: '24px', color: '#d1d5db', display: 'block', marginBottom: '8px' }} />
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>No dates selected yet</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '280px', overflowY: 'auto' }}>
                        {schedule.customDates.slice().sort().map((dateStr) => (
                          <div key={dateStr} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <i className="pi pi-calendar" style={{ color: '#3b82f6', fontSize: '12px' }} />
                              {formatDisplayDate(dateStr)}
                            </div>
                            <button onClick={() => removeCustomDate(dateStr)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px 4px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
                              <i className="pi pi-times" style={{ fontSize: '11px' }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {schedule.customDates.length > 0 && (
                      <button onClick={clearAllDates} style={{ marginTop: '10px', width: '100%', padding: '8px', background: 'none', border: '1px solid #fca5a5', borderRadius: '8px', color: '#ef4444', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>
                        <i className="pi pi-trash" style={{ marginRight: '6px', fontSize: '11px' }} />Clear all dates
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {schedule.customDates.length > 0 && (
                <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '14px 18px', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="pi pi-info-circle" style={{ color: '#3b82f6', fontSize: '16px' }} />
                  <p style={{ fontSize: '12px', color: '#1e40af', margin: 0 }}>
                    Fee reminders will be sent on <strong>{schedule.customDates.length}</strong> specific date{schedule.customDates.length !== 1 ? 's' : ''}.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fee Amount Visibility Card */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(30,64,175,0.08)', border: '1px solid rgba(59,130,246,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #f3f4f6' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: showFeeAmount ? 'linear-gradient(135deg,#0ea5e9 0%,#38bdf8 100%)' : 'linear-gradient(135deg,#64748b 0%,#94a3b8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>
            <i className={showFeeAmount ? 'pi pi-eye' : 'pi pi-eye-slash'} style={{ fontSize: '18px', color: 'white' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af', margin: 0 }}>Fee Amount Visibility</h2>
              <span style={{ fontSize: '11px', fontWeight: '600', borderRadius: '20px', padding: '2px 10px', background: showFeeAmount ? '#dbeafe' : '#f1f5f9', color: showFeeAmount ? '#1e40af' : '#475569', border: `1px solid ${showFeeAmount ? '#bfdbfe' : '#cbd5e1'}` }}>
                {showFeeAmount ? '✓ Visible' : '⊘ Hidden'}
              </span>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>Control whether students can see their outstanding fee amount on their portal</p>
          </div>
        </div>

        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '18px 20px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px 0' }}>
            <i className="pi pi-info-circle" style={{ marginRight: '6px', color: '#3b82f6' }} />What does this control?
          </p>
          <p style={{ fontSize: '13px', color: '#4b5563', margin: '0 0 10px 0', lineHeight: 1.7 }}>
            This setting controls whether the <strong>outstanding fee amount</strong> is displayed to students anywhere on their portal.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px', background: '#eff6ff', borderRadius: '10px', padding: '12px 14px', border: '1px solid #bfdbfe' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', margin: '0 0 4px 0' }}><i className="pi pi-eye" style={{ marginRight: '5px' }} /> When Enabled</p>
              <p style={{ fontSize: '12px', color: '#374151', margin: 0, lineHeight: 1.6 }}>Students see the exact amount due (e.g. <strong>₹4,500 pending</strong>) on their portal.</p>
            </div>
            <div style={{ flex: 1, minWidth: '200px', background: '#f8fafc', borderRadius: '10px', padding: '12px 14px', border: '1px solid #e2e8f0' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#475569', margin: '0 0 4px 0' }}><i className="pi pi-eye-slash" style={{ marginRight: '5px' }} /> When Disabled</p>
              <p style={{ fontSize: '12px', color: '#374151', margin: 0, lineHeight: 1.6 }}>Fee amounts are hidden. Students see that a payment is due but not the specific figure.</p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderRadius: '12px', border: `2px solid ${showFeeAmount ? '#bfdbfe' : '#e2e8f0'}`, background: showFeeAmount ? '#eff6ff' : '#f8fafc', transition: 'all 0.2s' }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: '0 0 2px 0' }}>Show fee amount to students</p>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
              {showFeeAmount ? 'Students can currently see their outstanding fee amount.' : 'Fee amounts are currently hidden from the student portal.'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: showFeeAmount ? '#1e40af' : '#64748b' }}>{showFeeAmount ? 'Visible' : 'Hidden'}</span>
            <GlobalSwitch value={showFeeAmount} onChange={() => setShowFeeAmount((prev) => !prev)} />
          </div>
        </div>
      </div>
    </>
  );
};

export default FeesReminderSettings;